import { ChangeDetectionStrategy, Component, DestroyRef, Inject, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';

import { DataConventionsService } from 'src/app/services/data-conventions.service';
import {
  DataConvention,
  DataConventionChangeReason,
  DEFAULT_DATA_CONVENTION,
  USER_SELECTABLE_DATA_CONVENTION_CHANGE_REASONS,
} from 'src/app/models/organizations/data-convention';
import {
  DataConventionCreatePayload,
  OrganizationDataConvention,
} from 'src/app/models/organizations/organization-data-convention';

export interface DataConventionSaveDialogData {
  organizationId: string;
  current: DataConvention | null;
  hasPriorConvention: boolean;
}

@Component({
  selector: 'app-data-convention-save-dialog',
  templateUrl: './data-convention-save-dialog.component.html',
  styleUrls: ['./data-convention-save-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.Default,
  standalone: false,
})
export class DataConventionSaveDialogComponent {
  private readonly destroyRef = inject(DestroyRef);

  readonly selectableReasons: readonly DataConventionChangeReason[] =
    USER_SELECTABLE_DATA_CONVENTION_CHANGE_REASONS;
  readonly reasonControl = new FormControl<DataConventionChangeReason | null>(null);
  readonly showReasonPicker: boolean;
  editableConvention: DataConvention;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: DataConventionSaveDialogData,
    private dialogRef: MatDialogRef<DataConventionSaveDialogComponent, OrganizationDataConvention | undefined>,
    private dataConventionsService: DataConventionsService,
  ) {
    this.showReasonPicker = data.hasPriorConvention;
    this.editableConvention = data.current ?? { ...DEFAULT_DATA_CONVENTION };
  }

  get canSave(): boolean {
    return this.showReasonPicker ? this.reasonControl.value !== null : true;
  }

  onDataConventionChange(convention: DataConvention): void {
    this.editableConvention = convention;
  }

  save(): void {
    if (!this.canSave) {
      return;
    }
    this.createConvention(this.buildPayload());
  }

  cancel(): void {
    this.dialogRef.close(undefined);
  }

  private buildPayload(): DataConventionCreatePayload {
    return { ...this.editableConvention, reason: this.resolveReason() };
  }

  private resolveReason(): DataConventionChangeReason {
    return this.showReasonPicker
      ? (this.reasonControl.value as DataConventionChangeReason)
      : 'InitialSetup';
  }

  private createConvention(payload: DataConventionCreatePayload): void {
    this.dataConventionsService
      .createDataConvention(this.data.organizationId, payload)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((result: OrganizationDataConvention) => this.dialogRef.close(result));
  }
}
